/* eslint-disable no-undef */
/**
 * Main menu functionality for responsive behaviors and dropdowns.
 *
 * @package  Embark
 * @since    1.0.0
 */

/**
 * Import dependencies.
 */
import $ from 'jquery';
import hoverintent from 'hoverintent';
import { disableScroll, enableScroll, debounce } from './helpers';

const { els, breakpoints } = embark;

/**
 * Menu items that have submenus.
 *
 * @type {NodeList}
 */
const menuItemsWithChildren = els.mainMenu ? els.mainMenu.querySelectorAll('.menu-item-has-children') : null;

/**
 * Class used for menu item triggers.
 *
 * @type {String}
 */
const menuItemTriggerClass = 'menu-item__trigger';

/**
 * Class used for mobile menu item 'back' li.
 *
 * @type {String}
 */
const menuItemBackClass = 'menu-item__back';

/**
 * Class added to <html> when menu is open.
 *
 * @type {String}
 */
const menuOpenHtmlClass = 'html--menu-open';

/**
 * Class added to navicon when menu is open.
 *
 * @type {String}
 */
const menuOpenNaviconClass = 'navicon__trigger--open';

/**
 * Class toggled on desktop middle-tier nav item for showing current child menu.
 */
const childMenuOpenClass = 'current_menu--open';

/**
 * Desktop: Toggle current menu item child items.
 */
function handleCurrentMenuItemChildren() {
	const currentParent = document.querySelector('.sub-menu--mega-menu > .current-menu-ancestor');
	const $siblingMenuItems = $(currentParent).siblings(':not(.menu-item-type-sidebar)');
	let previousState = '';

	if (currentParent) {
		// Auto-show the submenu for this item after a state change if this item is visible (untransformed).
		currentParent.ontransitionend = () => {
			const currentState = getComputedStyle(currentParent).transform;
			const currentParentStateChanged = !(currentState === previousState);
			if (currentParentStateChanged) {
				if (currentState === 'none') {
					currentParent.classList.add(childMenuOpenClass);
				} else {
					currentParent.classList.remove(childMenuOpenClass);
				}
			}
			previousState = currentState;
		};

		// Auto-hide/show the submenu when sibling menus are hovered.
		$siblingMenuItems.on({
			mouseenter: () => {
				currentParent.classList.remove(childMenuOpenClass);
			},
			mouseleave: () => {
				currentParent.classList.add(childMenuOpenClass);
			},
		});
	}
}

/**
 * Mobile: Toggle mobile submenus when a menu item link with a hash or the
 * menu item trigger is clicked.
 *
 * @param  {Event} e
 */
function handleMenuItemTriggerClick(e) {
	/**
	 * Prevents further propagation of the current event in the capturing and bubbling phases.
	 */
	e.stopPropagation();

	// Get the submenu based on what was clicked.
	let submenu;
	if (e.target.href) {
		const hash = e.target.href.substring(e.target.href.lastIndexOf('/') + 1);
		if (hash && hash === '#') {
			submenu = e.target.nextElementSibling.nextElementSibling;
		}
	} else if (e.target.classList.contains(menuItemTriggerClass)) {
		submenu = e.target.nextElementSibling;
	}

	// If we have a submenu related to this trigger, toggle it.
	if (submenu) {
		e.preventDefault();

		const $menuItem = $(submenu).closest('.menu-item');
		const $menu = $menuItem.closest('ul');

		$menuItem.find('> a').attr('aria-expanded', (index, attr) => (attr === 'true' ? 'false' : 'true'));
		$menuItem.toggleClass('open');
		$menu.toggleClass('opened');
	}
}

/**
 * Mobile: Delegate submenu "back" link clicks to their triggers.
 */
function handleMenuItemBackClick(e) {
	e.preventDefault();
	e.stopPropagation();
	e.currentTarget.parentNode.previousElementSibling.click();
}

/**
 * Add class to the first .current-menu-ancestor to avoid multiple being highlighted.
 *
 * @param  {Event} e
 */
function handleMultipleMenuAncestors() {
	const ancestorItems = document.querySelectorAll('.main-menu > .current-menu-ancestor');
	if (ancestorItems.length > 0) {
		ancestorItems[0].classList.add('current-menu-ancestor--primary');
	}
}

/**
 * Open the mobile menu.
 */
function openMobileMenu() {
	if (!els.naviconTrigger) {
		return;
	}

	disableScroll();

	// Kill any timers that would reset the menu position.
	if (window.mobileNavClose) clearTimeout(window.mobileNavClose);

	// Add indicator classes.
	els.html.classList.add(menuOpenHtmlClass);
	els.naviconTrigger.classList.add(menuOpenNaviconClass);
}

/**
 * Close the mobile menu.
 */
function closeMobileMenu() {
	if (!els.naviconTrigger) {
		return;
	}

	// Enable scroll if an ImageLightbox isn't open.
	if (!els.html.classList.contains('lightbox-open')) {
		enableScroll();
	}

	// Remove indicator classes.
	els.html.classList.remove(menuOpenHtmlClass);
	els.naviconTrigger.classList.remove(menuOpenNaviconClass);

	// After 2 seconds, reset the menu to its initial state.
	window.mobileNavClose = setTimeout(() => {
		const menuContainer = document.querySelector('.main-menu.opened');
		const openedMenu = document.querySelector('.main-menu .open');
		if (menuContainer) menuContainer.classList.remove('opened');
		if (openedMenu) openedMenu.classList.remove('open');
	}, 2000);
}

/**
 * Toggle the mobile menu.
 */
function toggleMobileMenu() {
	if (els.html.classList.contains(menuOpenHtmlClass)) {
		closeMobileMenu();
	} else {
		openMobileMenu();
	}
}

/**
 * Close the mobile menu when you click off of it.
 *
 * @param  {Event} e
 */
function handleMenuState(e) {
	if (!els.navicon) {
		return;
	}

	/**
	 * If the menu is open, and the event target is the nav or a child of the nav, exit quickly.
	 */
	if (
		els.html.classList.contains(menuOpenHtmlClass)
		&& (e.target === els.navContainer || $(e.target).closest(els.navContainer).length > 0)
	) {
		return;
	}

	/**
	 * If event target is the navicon or any of its children, trigger the toggle method.
	 */
	if (e.target === els.navicon || $(e.target).closest('.navicon__trigger').length > 0) {
		toggleMobileMenu();
	} else {
		/**
		 * Else, this is not the navicon trigger. Close the menu. User has clicked away.
		 */
		closeMobileMenu();
	}
}

/**
 * Update mobile menu top offset and close opened submenus depending on screensize.
 *
 * @param  {Event} e
 */
function handleMenuOnResize() {
	window.requestAnimationFrame(() => {
		// See https://dev.to/nirazanbasnet/dont-use-100vh-for-mobile-responsive-3o97
		document.documentElement.style.setProperty('--doc-height', `${window.innerHeight}px`);

		if (!breakpoints.isMobileMenu()) {
			closeMobileMenu();
		}
	});
}

/**
 * Add "Back" links to submenus to allow for mobile nav upwards movement.
 */
function enhanceMenuItemsWithChildren() {
	$(menuItemsWithChildren).each(function () {
		const $this = $(this);
		const title = $this.children('a').first().html();
		const $newElem = $(`<li class="menu-item ${menuItemBackClass}"><a href="javascript:void();" class="backlink">${title}</a></li>`);
		$this.children('ul').children('li:not(.menu-item-type-sidebar)').first().before($newElem);
	});
}

/**
 * Clone the items in the Utilty menu and stick them into the main menu (they're visible only on mobile),
 * then move lang-items to the end.
 */
function cloneUtilityMenu() {
	$('.site-header__utility-stripe .utility-menu').children().clone().addClass('menu-item-utility')
		.appendTo('.main-menu');
	$('.main-menu>.lang-item').appendTo('.main-menu');
	$('.main-menu').addClass(`lang-items-${$('.main-menu>.lang-item').length}`);
}

function handleMenuHovers() {
	const menus = document.querySelectorAll('.sub-menu--mega-menu>.menu-item-has-children');
	menus.forEach((item) => {
		hoverintent(item, () => {
			item.classList.add('hovered');
		}, () => {
			item.classList.remove('hovered');
		}).options({ timeout: 300 });
	});
}

/**
 * Initialize menu.
 *
 * @param  {Event} e [description]
 */
function initMainMenu() {
	enhanceMenuItemsWithChildren();
	cloneUtilityMenu();
	handleMultipleMenuAncestors();
	handleCurrentMenuItemChildren();
	handleMenuHovers();

	/**
	 * Add event listeners for menu items with submenus.
	 */
	if (menuItemsWithChildren) {
		// Children implies back links in mobile.
		document.querySelectorAll(`.${menuItemBackClass}`).forEach((item) => {
			item.addEventListener('click', handleMenuItemBackClick);
		});
		menuItemsWithChildren.forEach((item) => {
			item.addEventListener('click', handleMenuItemTriggerClick);
		});
	}

	// See https://dev.to/nirazanbasnet/dont-use-100vh-for-mobile-responsive-3o97
	document.documentElement.style.setProperty('--doc-height', `${window.innerHeight}px`);
}

initMainMenu();

window.addEventListener('resize', debounce(() => {
	handleMenuOnResize();
}, 50, false), { passive: true });

document.addEventListener('click', handleMenuState);
